import { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import { debounce } from 'debounce';
import { AwaitableContext } from 'contexts/AwaitableContext';

export interface UseDebounceStringPropertyOptions {
  value: string;
  path: string[];
  /**
   * Method which should be wrapped with "debounce".
   * Notice that you need to wrap this method with useCallback/useRef to pass the same method instance every time.
   *
   * For more information: https://www.freecodecamp.org/news/debounce-and-throttle-in-react-with-hooks/.
   */
  onChange: ((value: any, path: string[]) => void | Promise<void>) | ((value: any) => void | Promise<void>);
  /** milliseconds */
  interval?: number;
  /** True for numeric behavior */
  /** Helper function to ensure field data conforms to required format */
  formatHelper?: (value: string) => string | null;
  /** Helper function to ensure falsy values are initialized according to field requirements. */
  initialValueHelper?: (value: string | null) => string;
  /**
   * True to skip updating a value for a cycle, e.g. a field has focus, don't clobber what the user is typing.
   *
   * TODO make this _required_ so devs can't forget to track focus and introduce the typing wobble
   */
  skipValueUpdate: boolean;
}

function getInitialValue({
  value,
  formatHelper,
  initialValueHelper,
}: Pick<UseDebounceStringPropertyOptions, 'value' | 'formatHelper' | 'initialValueHelper'>) {
  let result = initialValueHelper?.(value) ?? value;
  result = formatHelper?.(result) ?? result;

  return result;
}

export function useDebounceStringProperty({
  value,
  path,
  formatHelper,
  initialValueHelper,
  onChange,
  interval = 500,
  skipValueUpdate,
}: UseDebounceStringPropertyOptions) {
  const [fieldValue, setFieldValue] = useState(
    getInitialValue({
      value,
      formatHelper,
      initialValueHelper,
    })
  );
  const debouncedOnChange = useMemo(() => debounce(onChange, interval), [interval, onChange]); //https://kyleshevlin.com/debounce-and-throttle-callbacks-with-react-hooks
  const { addPromise, removePromise } = useContext(AwaitableContext);
  const savingOnBlur = useRef(false);
  const id = path.join(',');

  // use effect to avoid overwriting initial state too aggressively
  // or overwriting what the user is typing.
  useEffect(() => {
    if (skipValueUpdate || savingOnBlur.current) {
      return;
    }
    savingOnBlur.current = false;
    const val = getInitialValue({
      value,
      formatHelper,
      initialValueHelper,
    });
    setFieldValue(val);
    // we want value updates only for value changes, really.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipValueUpdate, value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = formatHelper ? formatHelper(e.target.value) : e.target.value;
      setFieldValue(value === null ? '' : value);
      debouncedOnChange.clear();
      addPromise(
        id,
        new Promise(async resolve => {
          await debouncedOnChange(value, path);
          resolve();
        })
      );
    },
    [formatHelper, debouncedOnChange, addPromise, id, path]
  );

  const emitOnBlur = useCallback(async () => {
    // since a new source object should trigger 'value' to update,
    // this should work to update the value on blur when it is stale
    // from typing
    // console.warn('emit on blur?');
    if (fieldValue !== value) {
      debouncedOnChange.clear();
      // just in case debounced onChange network request is already scheduled:
      savingOnBlur.current = true;

      // notify anything that cares
      // console.log('Editor emit on blur!');
      addPromise(
        id,
        new Promise(async resolve => {
          // setTimeout(async () => { // DEBUG - slow this down to see that the other waits?
          await onChange(fieldValue, path);
          // console.warn('onChange is done');
          resolve();
          removePromise(id);
          // }, 2000);
        })
      );
    }
  }, [addPromise, debouncedOnChange, fieldValue, id, onChange, path, removePromise, value]);

  return { debouncedOnChange, emitOnBlur, fieldValue, setFieldValue, handleChange };
}
